import React, { useMemo, useState } from 'react';
import { VerbundTag } from './VerbundTag';
import { api } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { zielFuer, oeffnen } from './ziel';
import { MessageSquare, FolderOpen, FileText, FileSignature, CalendarClock, Wallet, PhoneCall, Calendar, CreditCard, Calculator, Siren, Users, User, Clock, Download, Loader2,
  Receipt, AlertTriangle, Banknote, CheckSquare, BadgeCheck, Mail, Hourglass, Timer, Ticket, PenLine, Monitor, ScrollText, ChevronDown, Search, ArrowUpRight } from 'lucide-react';

const ICON = { mandant: User, akte: FolderOpen, status: Clock, nachricht: MessageSquare, dokumente: FileText, signaturen: FileSignature, signiert: BadgeCheck, raten: CalendarClock,
  akonto: Wallet, rueckrufe: PhoneCall, rueckruf_notiz: PhoneCall, termine: Calendar, sepa: CreditCard, honorar: Calculator, notfall: Siren, nutzer: Users,
  rechnung: Receipt, mahnung: AlertTriangle, zahlung: Banknote, gutschrift: Banknote, zustimmung: CheckSquare, email: Mail, frist: Hourglass, zeit: Timer,
  ticket: Ticket, aenderung: PenLine, cobrowse: Monitor, rechtstext: ScrollText };

export const KATEGORIEN = [
  { k: 'finanzen', label: 'Finanzen', b: ['Rechnungen', 'Mahnungen', 'Zahlungen', 'Ratenpläne', 'Akontozahlungen', 'SEPA-Mandate', 'Honoraranfragen'] },
  { k: 'signaturen', label: 'Signaturen & Zustimmungen', b: ['E-Signaturen', 'Zustimmungen', 'Rechtstexte'] },
  { k: 'nachrichten', label: 'Nachrichten & E-Mails', b: ['Chats', 'E-Mails', 'Rückrufe', 'Co-Browsing'] },
  { k: 'fall', label: 'Fall & Termine', b: ['Akten', 'Termine', 'Tickets', 'Fristen', 'Zeiten', 'Notfallkontakte'] },
  { k: 'dokumente', label: 'Dokumente', b: ['Dokumente'] },
  { k: 'stammdaten', label: 'Stammdaten & Zugänge', b: ['Stammdaten', 'Website-Nutzer'] },
];
const katVon = (bereich) => (KATEGORIEN.find((c) => c.b.includes(bereich)) || KATEGORIEN[5]).k;
const ROT = ['mahnung'];
const GRUEN = ['zahlung', 'gutschrift', 'signiert'];

const fmtMonat = (iso) => new Date(iso).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
const fmtTag = (iso) => new Date(iso).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
const fmtTime = (iso) => new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

const zielVon = (e, mnr, onStammblatt) => {
  if (e.ziel) return { to: e.ziel };
  if (e.area) return zielFuer(e.area, e.id, mnr, e.case_id);
  if (e.bereich === 'Rückrufe') return { to: '/team/rueckrufe' };
  if (e.bereich === 'Tickets') return { to: '/team/tickets' };
  if (e.bereich === 'Stammdaten' && onStammblatt) return { fn: onStammblatt };
  return e.case_id ? { to: `/team-portal/${e.case_id}` } : null;
};

const punktFarbe = (e) => (e.typ === 'nachricht' ? (e.von_team ? 'bg-[#b8893d] text-white' : 'bg-[#1B3A5C] text-white')
  : ROT.includes(e.typ) ? 'bg-rose-50 border border-rose-200 text-rose-700' : GRUEN.includes(e.typ) ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'bg-white border border-gray-200 text-[#b8893d]');

const Ereignis = ({ e, i, mnr, onStammblatt, navigate }) => {
  const Icon = ICON[e.typ] || Clock;
  const ziel = zielVon(e, mnr, onStammblatt);
  const klick = () => { if (!ziel) return; if (ziel.fn) ziel.fn(); else oeffnen(navigate, ziel); };
  return (
    <li className="ml-6 mb-2.5 relative" data-testid={`zeitstrahl-event-${i}`}>
      <span className={`absolute -left-[37px] top-1 w-6 h-6 rounded-full flex items-center justify-center ring-4 ring-[#f5f8fa] ${punktFarbe(e)}`}><Icon className="w-3 h-3" /></span>
      <div role={ziel ? 'button' : undefined} tabIndex={ziel ? 0 : undefined} onClick={klick} onKeyDown={(ev) => ev.key === 'Enter' && klick()} data-testid={`zeitstrahl-event-open-${i}`}
        title={ziel ? 'Zum Vorgang springen' : undefined}
        className={`group bg-white border border-gray-200 px-3 py-2 transition-colors ${ziel ? 'cursor-pointer hover:border-[#b8893d]/60' : ''}`}>
        <div className="flex flex-wrap items-center gap-2 text-[10px] tracking-wider">
          <span className="text-[#b8893d] uppercase">{e.bereich}</span>
          {(e.thread || e.aktenzeichen) && <span className="font-mono text-[#34536b] bg-[#f5f8fa] px-1.5">{e.thread || `AZ ${e.aktenzeichen}`}</span>}
          <span className="ml-auto text-[#7a8a99]">{fmtTag(e.datum)} · {fmtTime(e.datum)}</span>
        </div>
        <p className="text-sm text-[#1B3A5C] font-medium mt-0.5 flex flex-wrap items-center gap-1.5"><VerbundTag nr={e.verbund_nr} />{e.titel}
          {ziel && <ArrowUpRight className="w-3.5 h-3.5 text-[#b8893d] opacity-0 group-hover:opacity-100 transition-opacity" />}</p>
        {e.text && <p className={`text-xs text-[#34536b] ${e.voll ? 'whitespace-pre-line mt-1 pl-2 border-l-2 border-[#b8893d]/40' : 'line-clamp-2'}`}>{e.text}</p>}
      </div>
    </li>
  );
};

const Monat = ({ monat, items, offen, onToggle, ...rest }) => (<>
  <li className="-ml-3 mb-2 mt-4 first:mt-0" data-testid={`zeitstrahl-monat-${monat.replace(/\s+/g, '-')}`}>
    <button type="button" onClick={onToggle} data-testid={`zeitstrahl-monat-toggle-${monat.replace(/\s+/g, '-')}`}
      className="flex items-center gap-2 bg-[#f5f8fa] pr-3 py-1 text-[11px] tracking-[0.2em] uppercase text-[#1B3A5C] hover:text-[#b8893d] transition-colors">
      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${offen ? '' : '-rotate-90'}`} />{monat}<span className="text-[#7a8a99] tracking-normal normal-case">({items.length})</span>
    </button>
  </li>
  {offen && items.map(({ e, i }) => <Ereignis key={`${e.typ}-${e.datum}-${i}`} e={e} i={i} {...rest} />)}
</>);

const Filter = ({ kat, setKat, events, q, setQ }) => (
  <div className="flex flex-wrap items-center gap-1.5">
    {[{ k: '', label: 'Alle Ereignisse' }, ...KATEGORIEN].map((c) => {
      const n = c.k ? events.filter((e) => katVon(e.bereich) === c.k).length : events.length;
      if (c.k && !n) return null;
      return (
        <button key={c.k || 'all'} type="button" onClick={() => setKat(c.k)} data-testid={`zeitstrahl-filter-${c.k || 'all'}`}
          className={`px-3 py-1 text-[11px] rounded-full border transition-colors ${kat === c.k ? 'bg-[#b8893d] border-[#b8893d] text-white' : 'bg-white border-gray-200 text-[#1B3A5C] hover:border-[#b8893d]'}`}>
          {c.label} ({n})
        </button>
      );
    })}
    <label className="relative ml-1"><Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[#7a8a99]" />
      <input value={q} onChange={(ev) => setQ(ev.target.value)} placeholder="Im Zeitstrahl suchen" data-testid="zeitstrahl-suche"
        className="pl-7 pr-2 py-1 text-[12px] border border-gray-200 bg-white w-48 focus:outline-none focus:border-[#b8893d]" />
    </label>
  </div>
);

export const Zeitstrahl = ({ events, mnr, caseId, onStammblatt }) => {
  const navigate = useNavigate();
  const [kat, setKat] = useState('');
  const [q, setQ] = useState('');
  const [zu, setZu] = useState({});
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfErr, setPdfErr] = useState('');
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return events.map((e, i) => ({ e, i })).filter(({ e }) => (!kat || katVon(e.bereich) === kat) && (!s || `${e.titel} ${e.text || ''} ${e.bereich}`.toLowerCase().includes(s)));
  }, [events, kat, q]);
  const monate = useMemo(() => shown.reduce((acc, x) => { const m = fmtMonat(x.e.datum); (acc[m] = acc[m] || []).push(x); return acc; }, {}), [shown]);
  const downloadPdf = async () => {
    setPdfBusy(true); setPdfErr('');
    try {
      const bereich = kat ? KATEGORIEN.find((c) => c.k === kat).b.join(',') : undefined;
      const r = await api.get(`/register/mandanten/${mnr}/zeitstrahl.pdf`, { params: { case_id: caseId || undefined, bereich }, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `Zeitstrahl_M-${mnr}.pdf`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { setPdfErr('PDF konnte nicht erstellt werden.'); }
    setPdfBusy(false);
  };

  return (
    <div data-testid="zeitstrahl">
      <div className="flex flex-wrap items-start gap-2 mb-5">
        <Filter kat={kat} setKat={setKat} events={events} q={q} setQ={setQ} />
        <button type="button" onClick={downloadPdf} disabled={pdfBusy || !shown.length} data-testid="zeitstrahl-pdf" title="Aktuelle Ansicht (Akte/Kategorie) als PDF herunterladen"
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] tracking-wider bg-[#1B3A5C] text-white hover:bg-[#b8893d] transition-colors disabled:opacity-40">
          {pdfBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} ALS PDF
        </button>
      </div>
      {pdfErr && <p className="mb-3 text-xs text-red-700" data-testid="zeitstrahl-pdf-error">{pdfErr}</p>}
      {shown.length === 0 && <p className="text-xs text-[#7a8a99] bg-white border border-dashed border-gray-200 px-4 py-6 text-center" data-testid="zeitstrahl-leer">Keine Ereignisse für diese Auswahl.</p>}
      <ol className="relative border-l-2 border-[#1B3A5C]/10 ml-3">
        {Object.entries(monate).map(([m, items]) => (
          <Monat key={m} monat={m} items={items} offen={!zu[m]} onToggle={() => setZu((z) => ({ ...z, [m]: !z[m] }))} mnr={mnr} onStammblatt={onStammblatt} navigate={navigate} />
        ))}
      </ol>
    </div>
  );
};

export default Zeitstrahl;
